'use client';

import type { AssistantSelectedEntity } from '@/app/lib/assistant-v2/context';

declare global {
  interface Window {
    __scarletAssistantPageContext?: Record<string, unknown>;
  }
}

/**
 * Page routes register live assistant context via AssistantButton on
 * `window.__scarletAssistantPageContext`. Ask / dock openers merge that
 * snapshot so the modal knows the active schedule, explore selection, etc.
 */
export function readAssistantPageContext(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  const pageContext = window.__scarletAssistantPageContext;
  const route = window.location.pathname;
  if (!pageContext || typeof pageContext !== 'object' || pageContext.route !== route) return { route };
  return { ...pageContext, route };
}

export function buildAssistantOpenContext(
  handoff: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    ...readAssistantPageContext(),
    ...handoff,
  };
}

const SKILL_PAGE_ACTIONS: Record<string, string[]> = {
  course_explore: ['open_course', 'compare_sections', 'add_to_schedule'],
  course_sniper: ['inspect_course', 'review_open_sections', 'manage_course_alert'],
  degree_planning: ['inspect_degree_plan', 'compare_requirements', 'update_degree_plan'],
  degree_search: ['open_degree', 'compare_degrees', 'add_degree_to_plan'],
  dining: ['inspect_dining_hall', 'compare_meals', 'review_menu'],
  research_opportunities: ['open_opportunity', 'compare_opportunities', 'review_eligibility'],
  scheduling: ['inspect_schedule', 'compare_sections', 'update_schedule'],
};

const ENTITY_KINDS = new Set([
  'course', 'professor', 'event', 'transit_route', 'transit_stop',
  'parking_permit', 'parking_lot', 'building', 'room', 'dining_location',
  'research_opportunity',
]);
const ENTITY_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9:._-]{0,119}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DEGREE_PLAN_ID_PATTERN = /^(?:[1-9]\d{0,11}|[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;
const HANDOFF_TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HANDOFF_STORAGE_PREFIX = 'scarlet.assistant.handoff.';
const HANDOFF_TTL_MS = 5 * 60 * 1000;
const MAX_STORED_HANDOFFS = 8;

export type AssistantWorkspaceHandoffPayload = {
  initialPrompt?: string;
  autoSend?: boolean;
  selectedScheduleId?: string;
  selectedDegreePlanId?: string;
  sourceRoute?: string;
  activeTerm?: string;
  selectedCourseIds?: string[];
  visibleSectionIds?: string[];
  selectedEntity?: AssistantSelectedEntity;
  availablePageActions?: string[];
  visibleView?: { label: string; value: string }[];
};

function boundedString(value: unknown, max: number): string {
  return typeof value === 'string' && value.length > 0 && value.length <= max ? value : '';
}

function selectedEntity(context: Record<string, unknown>) {
  const candidate = context.selectedEntity;
  if (!candidate || typeof candidate !== 'object') return null;
  const entity = candidate as Record<string, unknown>;
  const kind = boundedString(entity.kind, 40);
  const id = boundedString(entity.id, 120);
  const label = boundedString(entity.label, 160);
  if (!ENTITY_KINDS.has(kind) || !ENTITY_ID_PATTERN.test(id) || !label || /[\u0000-\u001f\u007f]/.test(label)) return null;
  return { kind: kind as AssistantSelectedEntity['kind'], id, label };
}

function compactList(value: unknown, max: number): string {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.length > 0 && item.length <= 80).slice(0, max).join(',')
    : '';
}

function collectRecordValues(value: unknown, keys: string[], max: number): string {
  if (!Array.isArray(value)) return '';
  const values = value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const match = keys.map((key) => boundedString(record[key], 80)).find(Boolean);
    return match ? [match] : [];
  });
  return [...new Set(values)].slice(0, max).join(',');
}

/**
 * Labelled facts a page publishes about what is on screen. Everything is bounded and
 * stripped of control characters, and anything malformed is dropped rather than
 * repaired, so a buggy caller degrades to publishing less rather than to smuggling
 * unbounded client state into the model's context.
 */
function visibleView(value: unknown): { label: string; value: string }[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const facts = value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const label = boundedString(record.label, 60);
    const text = boundedString(record.value, 200);
    if (!label || !text || /[\u0000-\u001f\u007f]/.test(label) || /[\u0000-\u001f\u007f]/.test(text)) return [];
    return [{ label, value: text }];
  }).slice(0, 12);
  return facts.length ? facts : undefined;
}

function splitBoundedList(value: string, max: number, pattern: RegExp): string[] | undefined {
  const items = value.split(',').filter((item) => pattern.test(item)).slice(0, max);
  return items.length ? items : undefined;
}

export function sanitizeAssistantHandoff(context: Record<string, unknown>): AssistantWorkspaceHandoffPayload {
  const payload: AssistantWorkspaceHandoffPayload = {};
  const prompt = typeof context.initialPrompt === 'string' ? context.initialPrompt.trim() : '';
  if (prompt && prompt.length <= 8_000) payload.initialPrompt = prompt;
  if (payload.initialPrompt && context.autoSend === true) payload.autoSend = true;
  if (typeof context.scheduleId === 'string' && UUID_PATTERN.test(context.scheduleId)) payload.selectedScheduleId = context.scheduleId;
  if (typeof context.selectedScheduleId === 'string' && UUID_PATTERN.test(context.selectedScheduleId)) payload.selectedScheduleId = context.selectedScheduleId;
  if (typeof context.planId === 'string' && DEGREE_PLAN_ID_PATTERN.test(context.planId)) payload.selectedDegreePlanId = context.planId;
  if (typeof context.selectedDegreePlanId === 'string' && DEGREE_PLAN_ID_PATTERN.test(context.selectedDegreePlanId)) payload.selectedDegreePlanId = context.selectedDegreePlanId;
  const sourceRoute = boundedString(context.route, 200) || boundedString(context.sourceRoute, 200);
  if (sourceRoute.startsWith('/')) payload.sourceRoute = sourceRoute;
  const activeTerm = boundedString(context.activeTerm, 32)
    || boundedString(context.termId, 32)
    || boundedString(context.selectedSemester, 32);
  if (/^[a-z0-9-]{4,32}$/i.test(activeTerm)) payload.activeTerm = activeTerm;
  const courseIds = compactList(context.selectedCourseIds, 25)
    || collectRecordValues(context.selectedCourses, ['baseId', 'id'], 25)
    || collectRecordValues(context.currentCourses, ['baseId'], 25)
    || boundedString(context.selectedCourseBaseId, 80);
  const sectionIds = compactList(context.visibleSectionIds, 50)
    || collectRecordValues(context.currentCourses, ['id'], 50);
  const skill = boundedString(context.skill, 80);
  const actions = compactList(context.availablePageActions, 40)
    || compactList(SKILL_PAGE_ACTIONS[skill], 40);
  payload.selectedCourseIds = splitBoundedList(courseIds, 25, /^[A-Za-z0-9:-]{5,80}$/);
  payload.visibleSectionIds = splitBoundedList(sectionIds, 50, /^[A-Za-z0-9:_-]{5,40}$/);
  const entity = selectedEntity(context);
  if (entity) payload.selectedEntity = entity;
  payload.availablePageActions = splitBoundedList(actions, 40, /^[a-z][a-z0-9_]{0,79}$/);
  payload.visibleView = visibleView(context.visibleView);
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));
}

function handoffToken(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function pruneAssistantWorkspaceHandoffs(now: number): void {
  const retained: Array<{ key: string; createdAt: number }> = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (!key?.startsWith(HANDOFF_STORAGE_PREFIX)) continue;
    try {
      const record = JSON.parse(sessionStorage.getItem(key) ?? '') as { version?: unknown; createdAt?: unknown };
      if (record.version !== 1 || typeof record.createdAt !== 'number' || now < record.createdAt || now - record.createdAt > HANDOFF_TTL_MS) {
        sessionStorage.removeItem(key);
        index -= 1;
        continue;
      }
      retained.push({ key, createdAt: record.createdAt });
    } catch {
      sessionStorage.removeItem(key);
      index -= 1;
    }
  }
  retained
    .sort((left, right) => right.createdAt - left.createdAt)
    .slice(MAX_STORED_HANDOFFS - 1)
    .forEach(({ key }) => sessionStorage.removeItem(key));
}

export function buildAssistantWorkspaceHref(context: Record<string, unknown> = {}): string {
  if (typeof window === 'undefined') return '/ai-assistant';
  const payload = sanitizeAssistantHandoff(context);
  if (Object.keys(payload).length === 0) return '/ai-assistant';
  const now = Date.now();
  pruneAssistantWorkspaceHandoffs(now);
  const token = handoffToken();
  sessionStorage.setItem(`${HANDOFF_STORAGE_PREFIX}${token}`, JSON.stringify({ version: 1, createdAt: now, payload }));
  return `/ai-assistant?handoff=${encodeURIComponent(token)}`;
}

export function consumeAssistantWorkspaceHandoff(token: string | null): AssistantWorkspaceHandoffPayload | undefined {
  if (typeof window === 'undefined' || !token || !HANDOFF_TOKEN_PATTERN.test(token)) return undefined;
  const key = `${HANDOFF_STORAGE_PREFIX}${token}`;
  const serialized = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (!serialized) return undefined;
  try {
    const record = JSON.parse(serialized) as { version?: unknown; createdAt?: unknown; payload?: unknown };
    if (record.version !== 1 || typeof record.createdAt !== 'number' || Date.now() - record.createdAt > HANDOFF_TTL_MS || Date.now() < record.createdAt) return undefined;
    if (!record.payload || typeof record.payload !== 'object' || Array.isArray(record.payload)) return undefined;
    return sanitizeAssistantHandoff(record.payload as Record<string, unknown>);
  } catch {
    return undefined;
  }
}
